import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaCheck,
  FaChevronDown,
  FaComments,
  FaInbox,
  FaPaperPlane,
  FaReply,
  FaSearch,
  FaUser,
  FaWhatsapp,
} from "react-icons/fa";
import { FaCheckDouble } from "react-icons/fa6";
import {
  changeChatStatus,
  fetchCannedMessagesByProject,
  fetchCustomerThread,
  fetchLiveChatContacts,
  fetchProjectAttributeNames,
  sendTextMessage,
} from "../../api/chat.api";
import { patchUpdatecontactById } from "../../api/customer.api";
import { useInboxSocket } from "../../hooks/useInboxSocket";
import { useProjectPhone } from "../../hooks/useProjectPhone";
import WhatsAppConnectPrompt from "./WhatsAppConnectPrompt";
import { getDisplayName, getUserEmail, getUserName, getUserType } from "../../utils/auth";
import {
  appendUniqueMessage,
  avatarLetter,
  deliveryTick,
  formatBubbleTime,
  formatDayLabel,
  formatListTime,
  formatMobile,
  formatTime,
  inboxAvatarTone,
  lastMessagePreview,
  messageDayKey,
  messagePreview,
} from "../../utils/chatHelpers";

const livechatAvatarTone = inboxAvatarTone;

const TABS = [
  { id: "active", label: "Active" },
  { id: "requesting", label: "Requesting" },
  { id: "intervened", label: "Intervened" },
];

const DETAIL_SECTIONS = [
  "Attribute",
  "Accounts Responsibility",
  "Campaign",
  "Notes",
  "Payment",
];

function contactAttributeEntries(contact) {
  const attrs = Array.isArray(contact?.attributes) ? contact.attributes : [];
  return attrs
    .map((attr, idx) => {
      if (attr == null) return null;
      if (typeof attr === "string") {
        const name = attr.trim();
        return name ? { key: `${name}-${idx}`, name, value: "" } : null;
      }
      const name = String(attr.name || attr.attribute_name || attr.key || "").trim();
      if (!name) return null;
      return {
        key: `${name}-${idx}`,
        name,
        value: attr.value ?? attr.action ?? "",
      };
    })
    .filter(Boolean);
}

function contactHasAttribute(contact, attributeName) {
  if (!attributeName) return true;
  const needle = attributeName.toLowerCase();
  return contactAttributeEntries(contact).some(
    (attr) => attr.name.toLowerCase() === needle,
  );
}

const LiveChat = () => {
  const { projectId } = useParams();
  const { phoneNoId, phoneReady, projectLoading, needsWhatsAppSetup } =
    useProjectPhone();
  const [activeTab, setActiveTab] = useState("active");
  const [allContacts, setAllContacts] = useState([]);
  const [counts, setCounts] = useState({ active: 0, requesting: 0, intervened: 0 });
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState("");
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const [attributeNames, setAttributeNames] = useState([]);
  const [attributeFilter, setAttributeFilter] = useState("");
  const [selectedAttribute, setSelectedAttribute] = useState("");
  const [attributeValue, setAttributeValue] = useState("");
  const [attributeSaving, setAttributeSaving] = useState(false);
  const [error, setError] = useState("");
  const [mobileThreadOpen, setMobileThreadOpen] = useState(false);
  const selectedRef = useRef(null);
  const contactIdsRef = useRef(new Set());
  const refreshRef = useRef(null);
  const loadedForRef = useRef(null);
  const everConnectedRef = useRef(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  useEffect(() => {
    if (!projectId) {
      setQuickReplies([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchCannedMessagesByProject(projectId);
        if (!cancelled) setQuickReplies(rows);
      } catch {
        if (!cancelled) setQuickReplies([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (!projectId && !phoneNoId) {
      setAttributeNames([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const names = await fetchProjectAttributeNames({ phoneNoId, projectId });
        if (!cancelled) setAttributeNames(names);
      } catch {
        if (!cancelled) setAttributeNames([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phoneNoId, projectId]);

  useEffect(() => {
    contactIdsRef.current = new Set(allContacts.map((c) => c._id));
  }, [allContacts]);

  const userType = getUserType() || "admin";
  const userEmail = getUserEmail();
  const senderUserName = getDisplayName() || getUserName() || "Agent";

  const applyContacts = useCallback((list, countsData) => {
    setAllContacts(Array.isArray(list) ? list : []);
    const c = countsData || {};
    setCounts({
      active: c.active ?? 0,
      requesting: c.requesting ?? 0,
      intervened: c.intervened ?? 0,
    });
  }, []);

  /** Initial page-load fetch. Everything after this arrives over the socket. */
  const loadContactsOnce = useCallback(async () => {
    if (projectLoading) return;
    if (!phoneNoId) {
      applyContacts([], {});
      setLoadingContacts(false);
      setError("");
      return;
    }
    if (loadedForRef.current === phoneNoId) return;
    loadedForRef.current = phoneNoId;
    setLoadingContacts(true);
    setError("");
    try {
      const [listRes, countRes] = await fetchLiveChatContacts(phoneNoId);
      const list = Array.isArray(listRes) ? listRes : listRes?.data || [];
      applyContacts(list, countRes?.data || countRes || {});
    } catch (err) {
      loadedForRef.current = null;
      setAllContacts([]);
      setError(err.message || "Failed to load contacts");
    } finally {
      setLoadingContacts(false);
    }
  }, [phoneNoId, projectLoading, applyContacts]);

  const handleMessageNew = useCallback((payload) => {
    if (!payload?.customerId || !payload?.message) return;
    const openId = selectedRef.current?._id;
    if (!contactIdsRef.current.has(payload.customerId)) {
      // First message from someone not in the list yet — pull the list over the socket.
      refreshRef.current?.();
      return;
    }
    setAllContacts((prev) =>
      prev.map((c) => {
        if (c._id !== payload.customerId) return c;
        return {
          ...c,
          lastMessage: {
            type: payload.message.type || "text",
            content: payload.message.message || payload.message.content,
            sentAt: payload.message.timestamp,
          },
          lastActive: String(payload.message.timestamp || c.lastActive),
          unReadMessageCount:
            openId === payload.customerId ? 0 : (c.unReadMessageCount || 0) + 1,
        };
      }),
    );
    if (openId === payload.customerId) {
      setMessages((prev) => appendUniqueMessage(prev, payload.message));
    }
  }, []);

  const handleContactUpdated = useCallback((payload) => {
    if (!payload?.customerId) return;
    setAllContacts((prev) =>
      prev.map((c) =>
        c._id === payload.customerId
          ? {
              ...c,
              status: payload.status ?? c.status,
              unReadMessageCount: payload.unReadMessageCount ?? c.unReadMessageCount,
              lastActive: payload.lastActive ?? c.lastActive,
            }
          : c,
      ),
    );
  }, []);

  const handleInboxCounts = useCallback((payload) => {
    setCounts({
      active: payload.active ?? 0,
      requesting: payload.requesting ?? 0,
      intervened: payload.intervened ?? 0,
    });
  }, []);

  const {
    connected,
    idle,
    authError,
    onlineAgents,
    joinThread,
    leaveThread,
    refreshContacts,
    sendMessage: socketSendMessage,
    changeStatus: socketChangeStatus,
  } = useInboxSocket({
    phoneNoId,
    projectId,
    enabled: phoneReady && Boolean(phoneNoId),
    onMessageNew: handleMessageNew,
    onContactUpdated: handleContactUpdated,
    onInboxCounts: handleInboxCounts,
  });

  useEffect(() => {
    refreshRef.current = async () => {
      try {
        const res = await refreshContacts();
        applyContacts(res.customers, res.counts);
      } catch {
        /* the socket is down; the list refreshes on reconnect */
      }
    };
  }, [refreshContacts, applyContacts]);

  useEffect(() => {
    loadContactsOnce();
  }, [loadContactsOnce]);

  // The first connect follows the page-load fetch, so only a *re*connect needs
  // a resync to pick up anything missed while the socket was down.
  useEffect(() => {
    if (!connected) return;
    if (!everConnectedRef.current) {
      everConnectedRef.current = true;
      return;
    }
    refreshRef.current?.();
  }, [connected]);

  useEffect(() => {
    setSelectedAttribute("");
    setAttributeValue("");
  }, [selected]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const tabContacts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allContacts
      .filter((c) => (c.status || "").toLowerCase() === activeTab)
      .filter((c) => {
        if (!q) return true;
        return (
          (c.name || "").toLowerCase().includes(q) ||
          String(c.waid || "").includes(q)
        );
      })
      .filter((c) => contactHasAttribute(c, attributeFilter))
      .sort(
        (a, b) => (b.unReadMessageCount || 0) - (a.unReadMessageCount || 0),
      );
  }, [allContacts, activeTab, search, attributeFilter]);

  const assignedAttributes = useMemo(
    () => contactAttributeEntries(selected),
    [selected],
  );

  const loadThread = useCallback(
    async (customerId) => {
      if (!customerId) return;
      setLoadingMessages(true);
      try {
        // Latest messages (backend returns chronological order).
        const thread = connected
          ? await joinThread(customerId, { limit: 100, skip: 0 })
          : (await fetchCustomerThread(customerId, { limit: 100, skip: 0 }))
              ?.messages;
        setMessages(Array.isArray(thread) ? thread : []);
      } catch {
        setMessages([]);
      } finally {
        setLoadingMessages(false);
      }
    },
    [connected, joinThread],
  );

  const selectContact = (contact) => {
    setSelected(contact);
    setMobileThreadOpen(true);
    loadThread(contact._id);
    setAllContacts((prev) =>
      prev.map((c) =>
        c._id === contact._id ? { ...c, unReadMessageCount: 0 } : c,
      ),
    );
  };

  const changeStatus = async (newStatus) => {
    if (!selected?.waid || !phoneNoId || !projectId) return;
    setStatusBusy(true);
    setError("");
    const payload = {
      selectedContact: selected.waid,
      selectedphonenumberid: phoneNoId,
      id: projectId,
      selectedtype: userType,
      selectedstatus: newStatus,
      extractedalphaemail: userEmail,
      mainBusinessObjectId: projectId,
    };
    try {
      if (connected) {
        await socketChangeStatus(payload);
      } else {
        await changeChatStatus(payload);
        loadedForRef.current = null;
        await loadContactsOnce();
      }
      leaveThread();
      setSelected(null);
      setMessages([]);
      // The server broadcasts contact:updated and inbox:counts, so the list and
      // the tab badges update themselves from here.
      if (newStatus === "intervened") setActiveTab("intervened");
      if (newStatus === "active") setActiveTab("active");
    } catch (err) {
      setError(err.message || "Could not update chat status");
    } finally {
      setStatusBusy(false);
    }
  };

  const persistContactAttributes = async (nextAttributes) => {
    if (!selected?._id) return;
    setAttributeSaving(true);
    setError("");
    try {
      await patchUpdatecontactById(
        { id: selected._id },
        { attributes_json: nextAttributes },
      );
      const updated = { ...selected, attributes: nextAttributes };
      setSelected(updated);
      setAllContacts((prev) =>
        prev.map((c) => (c._id === selected._id ? updated : c)),
      );
    } catch (err) {
      setError(err.message || "Could not update attributes");
    } finally {
      setAttributeSaving(false);
    }
  };

  const addAttributeToContact = async (e) => {
    e.preventDefault();
    const name = selectedAttribute.trim();
    if (!name || !selected?._id) return;
    const current = contactAttributeEntries(selected);
    if (current.some((attr) => attr.name.toLowerCase() === name.toLowerCase())) {
      setError("This attribute is already assigned.");
      return;
    }
    await persistContactAttributes([
      ...current.map(({ name: n, value }) => ({ name: n, value })),
      { name, value: attributeValue.trim() },
    ]);
    setSelectedAttribute("");
    setAttributeValue("");
  };

  const removeAttributeFromContact = async (name) => {
    if (!name || !selected?._id) return;
    const next = contactAttributeEntries(selected)
      .filter((attr) => attr.name !== name)
      .map(({ name: n, value }) => ({ name: n, value }));
    await persistContactAttributes(next);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || !selected?.waid || activeTab !== "intervened") return;
    setSending(true);
    setError("");
    const payload = {
      from: phoneNoId,
      to: selected.waid,
      message: text,
      timestamp: Math.floor(Date.now() / 1000),
      profileName: senderUserName,
      currentphonenumberId: phoneNoId,
      senderUserName,
    };
    try {
      const saved = connected
        ? await socketSendMessage(payload)
        : (await sendTextMessage(payload))?.data;
      setDraft("");
      // message:new also arrives over the socket; appendUniqueMessage keeps the
      // echo from duplicating the bubble.
      if (saved) {
        setMessages((prev) => appendUniqueMessage(prev, saved));
      }
    } catch (err) {
      setError(err.message || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const tabCount = (id) => counts[id] ?? 0;

  const accent = selected ? livechatAvatarTone(selected.name) : "green";

  const backToContactList = () => {
    setMobileThreadOpen(false);
    leaveThread();
    setSelected(null);
    setMessages([]);
  };

  if (projectLoading) {
    return (
      <div className="livechat-page livechat-page-loading">
        <div className="livechat-boot">
          <span className="livechat-boot-spinner" aria-hidden />
          <p>Loading inbox…</p>
        </div>
      </div>
    );
  }

  if (needsWhatsAppSetup) {
    return (
      <div className="livechat-page">
        <WhatsAppConnectPrompt
          projectId={projectId}
          title="Connect WhatsApp to use Team Inbox"
          description="Live chat shows conversations for your linked WhatsApp Business number. Connect your number in project settings to start receiving chats here."
        />
      </div>
    );
  }

  return (
    <div
      className={`livechat-page${
        mobileThreadOpen && selected ? " livechat-mobile-thread" : ""
      }`}
    >
      <aside className="livechat-contacts">
        <div className="livechat-inbox-header">
          <div>
            <h1 className="livechat-inbox-title">Team Inbox</h1>
            <p className="livechat-inbox-subtitle">WhatsApp conversations</p>
          </div>
          <span
            className={`livechat-status ${
              connected ? "is-live" : idle ? "is-idle" : "is-offline"
            }`}
          >
            <span className="livechat-status-dot" />
            {connected
              ? `${onlineAgents.length || 1} online`
              : idle
                ? "Paused"
                : "Connecting"}
          </span>
        </div>

        <div className="livechat-search-row">
          <div className="livechat-search-wrap">
            <FaSearch className="livechat-search-icon" />
            <input
              type="search"
              className="livechat-search-input"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="livechat-attribute-filter"
            value={attributeFilter}
            onChange={(e) => setAttributeFilter(e.target.value)}
            aria-label="Filter by attribute"
            title="Filter by attribute"
          >
            <option value="">Attribute</option>
            {attributeNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div className="livechat-tabs" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`livechat-tab livechat-tab-${tab.id} ${
                activeTab === tab.id ? "livechat-tab-selected" : ""
              }`}
              onClick={() => {
                setActiveTab(tab.id);
                setSelected(null);
                setMessages([]);
                setMobileThreadOpen(false);
                leaveThread();
              }}
            >
              {tab.label}
              <span className="livechat-tab-count">{tabCount(tab.id)}</span>
            </button>
          ))}
        </div>

        {error && <p className="biz-panel-error livechat-inline-error">{error}</p>}
        {authError && !error && (
          <p className="biz-panel-error livechat-inline-error">{authError}</p>
        )}

        <div className="livechat-list" role="list">
          {loadingContacts ? (
            <div className="livechat-list-skel" aria-hidden>
              {[1, 2, 3, 4, 5, 6].map((row) => (
                <div key={row} className="livechat-skel-row">
                  <div className="livechat-skel-avatar-sm" />
                  <div className="livechat-skel-lines">
                    <span className="livechat-skel-line livechat-skel-line-md" />
                    <span className="livechat-skel-line livechat-skel-line-sm" />
                  </div>
                </div>
              ))}
            </div>
          ) : tabContacts.length === 0 ? (
            <div className="livechat-empty-list">
              <FaInbox aria-hidden />
              <strong>No conversations</strong>
              <span>Nothing in {TABS.find((tab) => tab.id === activeTab)?.label || "this"} yet.</span>
            </div>
          ) : (
            tabContacts.map((c) => {
              const unread = (c.unReadMessageCount || 0) > 0;
              return (
                <button
                  key={c._id}
                  type="button"
                  className={`livechat-item${
                    selected?._id === c._id ? " livechat-item-active" : ""
                  }${unread ? " has-unread" : ""}`}
                  onClick={() => selectContact(c)}
                >
                  <div
                    className={`livechat-avatar livechat-avatar-${livechatAvatarTone(c.name)}`}
                  >
                    {avatarLetter(c.name)}
                  </div>
                  <div className="livechat-item-main">
                    <div className="livechat-item-top">
                      <span className="livechat-name">{c.name || "Unknown"}</span>
                      <span className="livechat-time">
                        {formatListTime(c.lastActive)}
                      </span>
                    </div>
                    <div className="livechat-item-bottom">
                      <span className="livechat-preview">
                        {lastMessagePreview(c)}
                      </span>
                      {unread && (
                        <span className="livechat-unread">
                          {c.unReadMessageCount}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </aside>

      <section className="livechat-messages">
        {!selected ? (
          <div className="livechat-messages-empty">
            <span className="livechat-empty-icon">
              <FaComments />
            </span>
            <div className="livechat-empty-copy">
              <strong>Select a conversation</strong>
              <span>Choose a contact from the inbox to view messages and reply.</span>
            </div>
          </div>
        ) : (
          <div className="livechat-thread">
            <div className="livechat-thread-header">
              <button
                type="button"
                className="livechat-back-btn"
                onClick={backToContactList}
                aria-label="Back to contacts"
              >
                <FaArrowLeft />
              </button>
              <div className={`livechat-avatar livechat-thread-avatar livechat-avatar-${accent}`}>
                {avatarLetter(selected.name)}
              </div>
              <div className="livechat-thread-identity">
                <strong>{selected.name || "Unknown contact"}</strong>
                <span className="livechat-thread-waid">
                  {formatMobile(selected.waid)}
                  {selected.lastActive
                    ? ` · Last active ${formatTime(selected.lastActive)}`
                    : ""}
                </span>
              </div>
              <span className={`livechat-status-chip livechat-status-chip-${activeTab}`}>
                {activeTab}
              </span>
              <div className="livechat-thread-actions">
                {activeTab === "requesting" && (
                  <button
                    type="button"
                    className="biz-btn-primary biz-btn-sm"
                    disabled={statusBusy}
                    onClick={() => changeStatus("intervened")}
                  >
                    Intervene
                  </button>
                )}
                {activeTab === "intervened" && (
                  <button
                    type="button"
                    className="biz-btn-outline biz-btn-sm"
                    disabled={statusBusy}
                    onClick={() => changeStatus("active")}
                  >
                    Resolve
                  </button>
                )}
              </div>
            </div>

            <div className="livechat-message-list">
              {loadingMessages ? (
                <p className="livechat-list-loading">Loading messages…</p>
              ) : messages.length === 0 ? (
                <div className="livechat-thread-empty">
                  <FaComments aria-hidden />
                  <strong>No messages yet</strong>
                  <span>Replies will appear here once the conversation starts.</span>
                </div>
              ) : (
                messages.map((msg, idx) => {
                  const ts = msg.createdAt || msg.timestamp;
                  const dayKey = messageDayKey(ts);
                  const prevDay =
                    idx > 0
                      ? messageDayKey(
                          messages[idx - 1].createdAt || messages[idx - 1].timestamp,
                        )
                      : "";
                  const showDay = Boolean(dayKey && dayKey !== prevDay);
                  const tick = msg.fromMe ? deliveryTick(msg.deliveryStatus) : null;
                  return (
                    <React.Fragment key={msg._id || msg.id || idx}>
                      {showDay && (
                        <div className="livechat-day-sep">
                          <span>{formatDayLabel(ts)}</span>
                        </div>
                      )}
                      <div
                        className={`livechat-message-bubble ${
                          msg.fromMe ? "outgoing" : "incoming"
                        }${
                          msg.fromMe && msg.deliveryStatus === "failed"
                            ? " is-failed"
                            : ""
                        }`}
                      >
                        <p>{messagePreview(msg)}</p>
                        <span className="livechat-message-meta">
                          <span className="livechat-message-time">
                            {formatBubbleTime(ts)}
                          </span>
                          {tick && (
                            <span
                              className={`livechat-ticks livechat-ticks-${String(
                                msg.deliveryStatus || "sent",
                              ).toLowerCase()}`}
                              title={tick.label}
                            >
                              {msg.deliveryStatus === "failed" ? (
                                "!"
                              ) : tick.icon === "double" ? (
                                <FaCheckDouble />
                              ) : (
                                <FaCheck />
                              )}
                            </span>
                          )}
                        </span>
                      </div>
                    </React.Fragment>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {activeTab === "intervened" ? (
              <div className="livechat-compose-wrap">
                {quickReplies.length > 0 && (
                  <div className="livechat-quick-replies">
                    <span className="livechat-quick-replies-label">
                      <FaReply aria-hidden />
                      Quick replies
                    </span>
                    <div className="livechat-quick-replies-chips">
                      {quickReplies.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          className="livechat-quick-reply-chip"
                          title={item.text}
                          disabled={sending}
                          onClick={() => setDraft(item.text)}
                        >
                          {item.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <form className="livechat-compose" onSubmit={sendMessage}>
                  <input
                    type="text"
                    placeholder="Type a message…"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    disabled={sending}
                  />
                  <button type="submit" disabled={sending || !draft.trim()} aria-label="Send">
                    <FaPaperPlane />
                  </button>
                </form>
              </div>
            ) : (
              <div className="livechat-compose-hint">
                <FaComments aria-hidden />
                <span>
                  {activeTab === "requesting"
                    ? "Take over this chat to reply from the inbox."
                    : "This conversation is resolved. Move it to Intervened to send a reply."}
                </span>
              </div>
            )}
          </div>
        )}
      </section>

      <aside className="livechat-details">
        {selected ? (
          <>
            <div className="livechat-details-profile">
              <div className="livechat-details-avatar-wrap">
                <div
                  className={`livechat-details-avatar livechat-avatar-${accent}`}
                >
                  {avatarLetter(selected.name)}
                </div>
                <span className="livechat-details-wa-icon" aria-hidden>
                  <FaWhatsapp />
                </span>
              </div>
              <div className="livechat-details-profile-text">
                <strong>{selected.name || "Unknown contact"}</strong>
                <span>{formatMobile(selected.waid)}</span>
                <span className={`livechat-status-chip livechat-status-chip-${activeTab}`}>
                  {activeTab}
                </span>
              </div>
            </div>

            {DETAIL_SECTIONS.map((label) => (
              <details key={label} className="livechat-details-accordion">
                <summary>
                  <span>{label}</span>
                  <FaChevronDown className="livechat-details-chevron" aria-hidden />
                </summary>
                {label === "Attribute" ? (
                  <div className="livechat-details-accordion-body livechat-attribute-panel">
                    <select
                      className="livechat-attribute-select"
                      value={selectedAttribute}
                      onChange={(e) => setSelectedAttribute(e.target.value)}
                      disabled={attributeSaving}
                      aria-label="Select an attribute"
                    >
                      <option value="">Select an attribute</option>
                      {attributeNames.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                    {attributeNames.length === 0 ? (
                      <p>No attributes defined for this project.</p>
                    ) : (
                      <form
                        className="livechat-attribute-add"
                        onSubmit={addAttributeToContact}
                      >
                        <input
                          type="text"
                          placeholder="Value (optional)"
                          value={attributeValue}
                          onChange={(e) => setAttributeValue(e.target.value)}
                          disabled={attributeSaving || !selectedAttribute}
                        />
                        <button
                          type="submit"
                          disabled={attributeSaving || !selectedAttribute}
                        >
                          Add
                        </button>
                      </form>
                    )}
                    {assignedAttributes.length > 0 ? (
                      <ul className="livechat-attribute-list">
                        {assignedAttributes.map((attr) => (
                          <li key={attr.key}>
                            <span>
                              <strong>{attr.name}</strong>
                              {attr.value ? `: ${attr.value}` : ""}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeAttributeFromContact(attr.name)}
                              disabled={attributeSaving}
                              aria-label={`Remove ${attr.name}`}
                            >
                              ×
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p>No attribute assigned to this contact.</p>
                    )}
                  </div>
                ) : (
                  <p className="livechat-details-accordion-body">
                    No {label.toLowerCase()} data available.
                  </p>
                )}
              </details>
            ))}
          </>
        ) : (
          <div className="livechat-details-empty">
            <span className="livechat-details-empty-icon">
              <FaUser aria-hidden />
            </span>
            <p className="livechat-details-title">Contact details</p>
            <p className="livechat-details-empty-copy">
              Select a conversation to see the customer profile, attributes, and notes.
            </p>
          </div>
        )}
      </aside>
    </div>
  );
};

export default LiveChat;
